import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';

/*
 * User-facing copy for every way the lookup can fail, keyed by the `reason` ref below.
 *
 * One map, because these strings render in two very different places: the desktop popover,
 * which can style and wrap them, and the mobile field's own value, which is a native
 * <select> option. The mobile side is the constraint - search_pill_field in
 * search-form.scss truncates that value to a single bold 16px line with an ellipsis, so
 * nothing here should run much past 30 characters or a phone cuts it off mid-word.
 *
 * None of them says "choose a spa below": in both places the list of spas is directly
 * beneath the message, so the instruction would be noise.
 */
export const NEARBY_MESSAGES = Object.freeze({
    denied: 'Location access is off',
    unsupported: "Can't access your location",
    failed: "Couldn't find your location",
    empty: 'No spas found near you',
});

/*
 * Shown while the lookup is running. Deliberately not in NEARBY_MESSAGES: that map is keyed
 * by `reason`, and this is the one message that is not a failure.
 *
 * This is the mobile wording, cut to the same length budget as the map above because it too
 * lands in the field's own value. The desktop popover says "Finding your nearest locations…"
 * in its own markup, which it has the width for. The two are meant to differ.
 */
export const NEARBY_LOCATING_MESSAGE = 'Finding your nearest…';

export function useNearestLocations(apiUrl, count = 3) {
    const status = ref('idle'); // idle | locating | loading | ready | error

    /*
     * Why the lookup produced nothing, or null when it produced something.
     *
     * Kept alongside status rather than folded into it because 'empty' is not an error: the
     * request succeeded and honestly returned no spas. Telling the customer something failed
     * would be a lie, but they still need to be told, so it earns a reason without an error
     * status.
     */
    const reason = ref(null); // null | denied | unsupported | failed | empty

    const locations = ref([]);
    const permissionGranted = ref(false);

    const message = computed(() => NEARBY_MESSAGES[reason.value] ?? null);

    let permissionStatus = null;
    const onPermissionChange = () => {
        permissionGranted.value = permissionStatus.state === 'granted';
    };

    onMounted(async () => {
        if (!navigator.permissions?.query) return;

        try {
            permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
            onPermissionChange();
            permissionStatus.addEventListener('change', onPermissionChange);
        } catch {
            // Permissions API present but 'geolocation' isn't queryable here - leave
            // permissionGranted false so the caller falls back to a manual prompt.
        }
    });

    onBeforeUnmount(() => permissionStatus?.removeEventListener('change', onPermissionChange));

    function getPosition() {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
        });
    }

    /**
     * Runs the lookup and resolves with the nearest locations, or an empty array if anything
     * went wrong.
     *
     * Resolving with the list matters as much as the refs do. A caller that wants to act on
     * the result - selecting the closest spa, say - can await this promise and read what
     * THIS call returned, rather than watching shared state that a different caller may have
     * written in the meantime. See LocationSelect.vue, where that distinction is the whole
     * guard keeping an automatic lookup from changing the customer's chosen spa.
     */
    async function fetchNearest() {
        reason.value = null;
        locations.value = [];

        /*
         * Checked before status moves, because the call inside getPosition() is a property
         * access on a possibly-undefined object. Browsers withhold navigator.geolocation
         * entirely on a non-secure origin, so this fires on plain HTTP - local dev over
         * http://spa.bann.ws included - where it would otherwise surface as a TypeError
         * caught below and reported as an ordinary lookup failure.
         */
        if (! navigator.geolocation) {
            status.value = 'error';
            reason.value = 'unsupported';

            return [];
        }

        status.value = 'locating';

        let position;

        try {
            position = await getPosition();
        } catch (error) {
            status.value = 'error';

            /*
             * 1 is GeolocationPositionError.PERMISSION_DENIED, compared as a literal because
             * the interface is not exposed as a global in every browser. Codes 2 (position
             * unavailable) and 3 (timeout) share one message on purpose: the remedy for both
             * is "try again", so a third wording buys the customer nothing.
             */
            reason.value = error?.code === 1 ? 'denied' : 'failed';

            /*
             * Otherwise only ever written by the Permissions API listener, which Safari never
             * reaches (see onMounted). Stating it on a refusal stops a caller's automatic
             * lookup firing again against a permission we have just been told we do not have.
             */
            if (reason.value === 'denied') {
                permissionGranted.value = false;
            }

            return [];
        }

        permissionGranted.value = true;
        status.value = 'loading';

        try {
            const response = await axios.get(apiUrl, {
                params: {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    count,
                },
            });

            locations.value = response.data.locations ?? [];
        } catch {
            status.value = 'error';
            reason.value = 'failed';

            return [];
        }

        status.value = 'ready';

        if (! locations.value.length) {
            reason.value = 'empty';
        }

        return locations.value;
    }

    /*
     * Back to the state this mounted in, so the lookup can be offered again after a failure.
     *
     * Needed because the mobile control is a <select> option: once it is the selected one,
     * tapping it again fires no change event. The customer's next real choice of spa is the
     * only moment left to clear the error, and this is what that moment calls.
     */
    function reset() {
        status.value = 'idle';
        reason.value = null;
        locations.value = [];
    }

    return { status, reason, message, locations, permissionGranted, fetchNearest, reset };
}
